import { z } from 'zod';
/* ==========================================================================
 *  Premium PDF report export (ISSUE-052 / FR-9.3 / ADR-0012 §3 reports_export)
 *
 *  A formatted, shareable nutrition report (weekly or monthly) rendered to PDF,
 *  generated async and delivered via a signed download URL — the deliverable
 *  behind the existing `reports_export` premium gate (M-C2 gating matrix).
 *
 *  DISTINCT from the GDPR data export (Art. 20, `export_job`): that is raw data
 *  for portability and is never gated; this is a presentation artefact and is
 *  premium-only. The job lifecycle deliberately mirrors the export flow
 *  (pending → processing → ready | failed, signed URL, 7-day TTL) so both reuse
 *  the same async-generate → B2 → signed-link machinery.
 *
 *  CONTRACT TYPES only — zod is the single source of truth. Browser-safe: no
 *  server-only or provider imports.
 * ========================================================================== */
/** Which rollup a report covers. `week` and `month` mirror the stored summary
 *  periods (ADR-0004 / ISSUE-030 / ISSUE-032); `day` reports are not offered. */
export const reportPeriodSchema = z.enum(['week', 'month']);
/** Body for `POST /me/reports/pdf`. `date` is any ISO date inside the desired
 *  period; the server snaps it to the period start (Monday / 1st-of-month).
 *  Omitted → the most recent completed period. */
export const createReportRequestSchema = z.object({
    period: reportPeriodSchema,
    date: z.string().date().optional(),
});
/** Job lifecycle — same shape as the GDPR export job (gdpr.ts) by design. */
export const reportJobStatusSchema = z.enum([
    'pending', // enqueued
    'processing', // rendering the PDF
    'ready', // uploaded, signed link live
    'expired', // link past its 7-day TTL
    'failed',
]);
export const reportJobSchema = z.object({
    id: z.string().uuid(),
    status: reportJobStatusSchema,
    period: reportPeriodSchema,
    /** ISO date of the first day of the covered period (Monday / 1st-of-month). */
    periodStart: z.string().date(),
    requestedAt: z.string().datetime(),
    /** Signed URL; present only when `status === 'ready'`. Never logged. */
    downloadUrl: z.string().url().nullable().default(null),
    /** Link TTL — 7 days from ready (mirrors the GDPR export TTL). */
    expiresAt: z.string().datetime().nullable().default(null),
});
//# sourceMappingURL=report-export.js.map